import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { propertyApi, tenantApi, etatDesLieuxApi } from '../../services/api';
import StateDropdown from '../StateDropdown';
import RoomInspectionSection from './RoomInspectionSection';

const EtatDesLieuxForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [currentStep, setCurrentStep] = useState(1);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    // Step 1: Selection
    propertyId: '',
    tenantId: '',
    type: 'entrée',
    inspectionDate: new Date().toISOString().split('T')[0],

    // Step 2: Basic info
    propertyAddress: '',
    landlordName: '',
    landlordAddress: '',
    tenantName: '',
    tenantAddress: '',
    previousOccupantName: '',

    // Step 3: Meter readings
    meterReadings: {
      electricityNumber: '',
      electricityHPleines: 0,
      electricityHCreuses: 0,
      gasNumber: '',
      gasReading: 0,
      waterHot: 0,
      waterCold: 0,
    },

    // Step 4: Energy equipment
    heatingType: '',
    heatingTypeOther: '',
    waterHeatingType: '',
    waterHeatingTypeOther: '',
    boilerState: '',
    boilerLastMaintenance: '',
    waterHeaterState: '',

    // Step 5: Keys
    keys: [{ type: '', number: 0, comments: '' }],

    // Step 6: Private areas
    privateAreas: {
      cave: { entryState: '', exitState: '', number: '', comments: '' },
      parking: { entryState: '', exitState: '', number: '', comments: '' },
      garden: { entryState: '', exitState: '', number: '', comments: '' },
      balcony: { entryState: '', exitState: '', number: '', comments: '' },
    },

    // Step 7: Other equipment
    otherEquipment: {
      doorbell: { entryState: '', exitState: '', comments: '' },
      mailbox: { entryState: '', exitState: '', comments: '' },
    },

    // Step 8: Rooms
    rooms: [],

    // Step 9: General comments
    generalComments: '',
  });

  const steps = [
    { number: 1, title: 'Sélection', description: 'Bien, locataire, type' },
    { number: 2, title: 'Informations', description: 'Adresses et parties' },
    { number: 3, title: 'Compteurs', description: 'Électricité, gaz, eau' },
    { number: 4, title: 'Équipements', description: 'Chauffage et énergie' },
    { number: 5, title: 'Clés', description: 'Inventaire des clés' },
    { number: 6, title: 'Parties privatives', description: 'Cave, parking, etc.' },
    { number: 7, title: 'Autres équipements', description: 'Sonnette, boîte aux lettres' },
    { number: 8, title: 'Pièces', description: 'Inspection détaillée' },
    { number: 9, title: 'Validation', description: 'Commentaires et révision' },
  ];

  useEffect(() => {
    fetchProperties();
    if (isEditMode) {
      fetchEtatDesLieux();
    }
  }, [id]);

  useEffect(() => {
    if (formData.propertyId) {
      fetchTenantsByProperty(formData.propertyId);
    }
  }, [formData.propertyId]);

  const fetchProperties = async () => {
    try {
      const response = await propertyApi.getAll();
      setProperties(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des biens');
    }
  };

  const fetchTenantsByProperty = async (propertyId) => {
    try {
      const response = await tenantApi.getByProperty(propertyId);
      setTenants(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des locataires');
    }
  };

  const fetchEtatDesLieux = async () => {
    try {
      const response = await etatDesLieuxApi.getById(id);
      const etat = response.data;

      // Parse JSON fields
      const meterReadings = etat.meterReadings ? JSON.parse(etat.meterReadings) : formData.meterReadings;
      const keys = etat.keys ? JSON.parse(etat.keys) : formData.keys;
      const privateAreas = etat.privateAreas ? JSON.parse(etat.privateAreas) : formData.privateAreas;
      const otherEquipment = etat.otherEquipment ? JSON.parse(etat.otherEquipment) : formData.otherEquipment;
      const rooms = etat.rooms ? JSON.parse(etat.rooms) : formData.rooms;

      setFormData({
        ...etat,
        inspectionDate: etat.inspectionDate || new Date().toISOString().split('T')[0],
        meterReadings,
        keys,
        privateAreas,
        otherEquipment,
        rooms,
      });
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'état des lieux');
      navigate('/etat-des-lieux');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData({
      ...formData,
      [parent]: { ...formData[parent], [field]: value },
    });
  };

  const handleArrayChange = (field, index, subField, value) => {
    const updatedArray = [...formData[field]];
    updatedArray[index][subField] = value;
    setFormData({ ...formData, [field]: updatedArray });
  };

  const addKey = () => {
    setFormData({
      ...formData,
      keys: [...formData.keys, { type: '', number: 0, comments: '' }],
    });
  };

  const removeKey = (index) => {
    const updatedKeys = formData.keys.filter((_, i) => i !== index);
    setFormData({ ...formData, keys: updatedKeys });
  };

  const handlePrivateAreaChange = (area, field, value) => {
    setFormData({
      ...formData,
      privateAreas: {
        ...formData.privateAreas,
        [area]: { ...formData.privateAreas[area], [field]: value },
      },
    });
  };

  const handleOtherEquipmentChange = (equipment, field, value) => {
    setFormData({
      ...formData,
      otherEquipment: {
        ...formData.otherEquipment,
        [equipment]: { ...formData.otherEquipment[equipment], [field]: value },
      },
    });
  };

  const handlePropertyChange = (propertyId) => {
    const property = properties.find(p => p.id === parseInt(propertyId));
    if (property) {
      setFormData({
        ...formData,
        propertyId,
        propertyAddress: `${property.address}, ${property.city}, ${property.postalCode}`,
      });
    } else {
      handleInputChange('propertyId', propertyId);
    }
  };

  const handleTenantChange = (tenantId) => {
    const tenant = tenants.find(t => t.id === parseInt(tenantId));
    if (tenant) {
      setFormData({
        ...formData,
        tenantId,
        tenantName: `${tenant.firstName} ${tenant.lastName}`,
        tenantAddress: tenant.email || '',
      });
    } else {
      handleInputChange('tenantId', tenantId);
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        meterReadings: JSON.stringify(formData.meterReadings),
        keys: JSON.stringify(formData.keys),
        privateAreas: JSON.stringify(formData.privateAreas),
        otherEquipment: JSON.stringify(formData.otherEquipment),
        rooms: JSON.stringify(formData.rooms),
      };

      if (isEditMode) {
        await etatDesLieuxApi.update(id, submitData);
        toast.success('État des lieux mis à jour avec succès');
      } else {
        await etatDesLieuxApi.create(submitData);
        toast.success('État des lieux créé avec succès');
      }

      navigate('/etat-des-lieux');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const isEntree = formData.type === 'entrée';

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Selection
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bien immobilier *
              </label>
              <select
                value={formData.propertyId}
                onChange={(e) => handlePropertyChange(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              >
                <option value="">Sélectionner un bien...</option>
                {properties.map(property => (
                  <option key={property.id} value={property.id}>
                    {property.name} - {property.address}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Locataire *
              </label>
              <select
                value={formData.tenantId}
                onChange={(e) => handleTenantChange(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
                disabled={!formData.propertyId}
              >
                <option value="">Sélectionner un locataire...</option>
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.firstName} {tenant.lastName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type d'état des lieux *
              </label>
              <select
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              >
                <option value="entrée">Entrée (début de location)</option>
                <option value="sortie">Sortie (fin de location)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date d'inspection *
              </label>
              <input
                type="date"
                value={formData.inspectionDate}
                onChange={(e) => handleInputChange('inspectionDate', e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>
          </div>
        );

      case 2: // Basic info
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse du bien *
              </label>
              <input
                type="text"
                value={formData.propertyAddress}
                onChange={(e) => handleInputChange('propertyAddress', e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du bailleur *
                </label>
                <input
                  type="text"
                  value={formData.landlordName}
                  onChange={(e) => handleInputChange('landlordName', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse du bailleur *
                </label>
                <input
                  type="text"
                  value={formData.landlordAddress}
                  onChange={(e) => handleInputChange('landlordAddress', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du locataire *
                </label>
                <input
                  type="text"
                  value={formData.tenantName}
                  onChange={(e) => handleInputChange('tenantName', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse du locataire *
                </label>
                <input
                  type="text"
                  value={formData.tenantAddress}
                  onChange={(e) => handleInputChange('tenantAddress', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom de l'ancien occupant
              </label>
              <input
                type="text"
                value={formData.previousOccupantName}
                onChange={(e) => handleInputChange('previousOccupantName', e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
        );

      case 3: // Meter readings
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Électricité</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  N° compteur
                </label>
                <input
                  type="text"
                  value={formData.meterReadings.electricityNumber}
                  onChange={(e) => handleNestedChange('meterReadings', 'electricityNumber', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Heures Pleines
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.meterReadings.electricityHPleines}
                  onChange={(e) => handleNestedChange('meterReadings', 'electricityHPleines', parseFloat(e.target.value) || 0)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Heures Creuses
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.meterReadings.electricityHCreuses}
                  onChange={(e) => handleNestedChange('meterReadings', 'electricityHCreuses', parseFloat(e.target.value) || 0)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>

            <h3 className="text-lg font-semibold text-gray-800 mt-6">Gaz naturel</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  N° compteur
                </label>
                <input
                  type="text"
                  value={formData.meterReadings.gasNumber}
                  onChange={(e) => handleNestedChange('meterReadings', 'gasNumber', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relevé
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.meterReadings.gasReading}
                  onChange={(e) => handleNestedChange('meterReadings', 'gasReading', parseFloat(e.target.value) || 0)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>

            <h3 className="text-lg font-semibold text-gray-800 mt-6">Eau</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Eau chaude (m³)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.meterReadings.waterHot}
                  onChange={(e) => handleNestedChange('meterReadings', 'waterHot', parseFloat(e.target.value) || 0)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Eau froide (m³)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.meterReadings.waterCold}
                  onChange={(e) => handleNestedChange('meterReadings', 'waterCold', parseFloat(e.target.value) || 0)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        );

      case 4: // Energy equipment
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type de chauffage
                </label>
                <select
                  value={formData.heatingType}
                  onChange={(e) => handleInputChange('heatingType', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">Sélectionner...</option>
                  <option value="électrique">Électrique</option>
                  <option value="gaz">Gaz</option>
                  <option value="collectif">Collectif</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              {formData.heatingType === 'autre' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Préciser
                  </label>
                  <input
                    type="text"
                    value={formData.heatingTypeOther}
                    onChange={(e) => handleInputChange('heatingTypeOther', e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type d'eau chaude
                </label>
                <select
                  value={formData.waterHeatingType}
                  onChange={(e) => handleInputChange('waterHeatingType', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">Sélectionner...</option>
                  <option value="électrique">Électrique</option>
                  <option value="gaz">Gaz</option>
                  <option value="collectif">Collectif</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              {formData.waterHeatingType === 'autre' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Préciser
                  </label>
                  <input
                    type="text"
                    value={formData.waterHeatingTypeOther}
                    onChange={(e) => handleInputChange('waterHeatingTypeOther', e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>

            <h3 className="text-lg font-semibold text-gray-800 mt-6">Équipements de chauffage</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  État de la chaudière
                </label>
                <input
                  type="text"
                  value={formData.boilerState}
                  onChange={(e) => handleInputChange('boilerState', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Bon, Passable, etc."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date dernier entretien
                </label>
                <input
                  type="date"
                  value={formData.boilerLastMaintenance}
                  onChange={(e) => handleInputChange('boilerLastMaintenance', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  État ballon d'eau chaude
                </label>
                <input
                  type="text"
                  value={formData.waterHeaterState}
                  onChange={(e) => handleInputChange('waterHeaterState', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Bon, Passable, etc."
                />
              </div>
            </div>
          </div>
        );

      case 5: // Keys
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Inventaire des clés</h3>
              <button
                type="button"
                onClick={addKey}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                + Ajouter une clé
              </button>
            </div>

            {formData.keys.map((key, index) => (
              <div key={index} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-start mb-3">
                  <span className="text-sm font-medium text-gray-700">Clé #{index + 1}</span>
                  {formData.keys.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeKey(index)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Supprimer
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type de clé / badge
                    </label>
                    <input
                      type="text"
                      value={key.type}
                      onChange={(e) => handleArrayChange('keys', index, 'type', e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Ex: Clé d'entrée, Badge parking..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre
                    </label>
                    <input
                      type="number"
                      value={key.number}
                      onChange={(e) => handleArrayChange('keys', index, 'number', parseInt(e.target.value) || 0)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Commentaires
                    </label>
                    <input
                      type="text"
                      value={key.comments}
                      onChange={(e) => handleArrayChange('keys', index, 'comments', e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 6: // Private areas
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Parties privatives attachées au logement</h3>

            {['cave', 'parking', 'garden', 'balcony'].map((area) => {
              const labels = {
                cave: 'Cave',
                parking: 'Parking / Box / Garage',
                garden: 'Jardin',
                balcony: 'Balcon / Terrasse'
              };

              return (
                <div key={area} className="border border-gray-300 rounded-lg p-4">
                  <h4 className="font-medium text-gray-800 mb-3">{labels[area]}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        État d'entrée
                      </label>
                      <StateDropdown
                        value={formData.privateAreas[area].entryState}
                        onChange={(e) => handlePrivateAreaChange(area, 'entryState', e.target.value)}
                        name={`privateAreas.${area}.entryState`}
                      />
                    </div>
                    {!isEntree && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          État de sortie
                        </label>
                        <StateDropdown
                          value={formData.privateAreas[area].exitState}
                          onChange={(e) => handlePrivateAreaChange(area, 'exitState', e.target.value)}
                          name={`privateAreas.${area}.exitState`}
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Numéro
                      </label>
                      <input
                        type="text"
                        value={formData.privateAreas[area].number}
                        onChange={(e) => handlePrivateAreaChange(area, 'number', e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Commentaires
                      </label>
                      <input
                        type="text"
                        value={formData.privateAreas[area].comments}
                        onChange={(e) => handlePrivateAreaChange(area, 'comments', e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );

      case 7: // Other equipment
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Autres équipements et aménagements</h3>

            {['doorbell', 'mailbox'].map((equipment) => {
              const labels = {
                doorbell: 'Sonnette / Interphone',
                mailbox: 'Boîte aux lettres'
              };

              return (
                <div key={equipment} className="border border-gray-300 rounded-lg p-4">
                  <h4 className="font-medium text-gray-800 mb-3">{labels[equipment]}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        État d'entrée
                      </label>
                      <StateDropdown
                        value={formData.otherEquipment[equipment].entryState}
                        onChange={(e) => handleOtherEquipmentChange(equipment, 'entryState', e.target.value)}
                        name={`otherEquipment.${equipment}.entryState`}
                      />
                    </div>
                    {!isEntree && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          État de sortie
                        </label>
                        <StateDropdown
                          value={formData.otherEquipment[equipment].exitState}
                          onChange={(e) => handleOtherEquipmentChange(equipment, 'exitState', e.target.value)}
                          name={`otherEquipment.${equipment}.exitState`}
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Commentaires
                      </label>
                      <input
                        type="text"
                        value={formData.otherEquipment[equipment].comments}
                        onChange={(e) => handleOtherEquipmentChange(equipment, 'comments', e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );

      case 8: // Rooms
        return (
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Inspection des pièces</h3>
            <RoomInspectionSection
              rooms={formData.rooms}
              onChange={(rooms) => handleInputChange('rooms', rooms)}
              etatType={formData.type}
            />
          </div>
        );

      case 9: // Validation
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Commentaires généraux et validation</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Commentaires généraux
              </label>
              <textarea
                value={formData.generalComments}
                onChange={(e) => handleInputChange('generalComments', e.target.value)}
                rows={6}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Ajoutez vos commentaires généraux sur l'état des lieux..."
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Récapitulatif</h4>
              <div className="text-sm text-blue-800 space-y-1">
                <p><strong>Type:</strong> {formData.type === 'entrée' ? 'Entrée' : 'Sortie'}</p>
                <p><strong>Date:</strong> {formData.inspectionDate}</p>
                <p><strong>Bien:</strong> {formData.propertyAddress}</p>
                <p><strong>Locataire:</strong> {formData.tenantName}</p>
                <p><strong>Pièces inspectées:</strong> {formData.rooms.length}</p>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                ⚠️ Après l'enregistrement, vous pourrez faire signer l'état des lieux par le bailleur et le locataire, puis générer le PDF final.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-6">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              {isEditMode ? 'Modifier l\'état des lieux' : 'Nouvel état des lieux'}
            </h1>
            <p className="mt-2 text-gray-600">
              Étape {currentStep} sur {steps.length}
            </p>
          </div>

          {/* Progress indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.number} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    currentStep === step.number
                      ? 'bg-blue-600 text-white'
                      : currentStep > step.number
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-300 text-gray-600'
                  }`}>
                    {currentStep > step.number ? '✓' : step.number}
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-12 h-1 mx-2 ${
                      currentStep > step.number ? 'bg-green-600' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 text-center">
              <p className="text-sm font-medium text-gray-900">{steps[currentStep - 1].title}</p>
              <p className="text-xs text-gray-500">{steps[currentStep - 1].description}</p>
            </div>
          </div>

          {/* Form content */}
          <form onSubmit={handleSubmit}>
            <div className="mb-8">
              {renderStepContent()}
            </div>

            {/* Navigation buttons */}
            <div className="flex justify-between border-t border-gray-200 pt-6">
              <button
                type="button"
                onClick={prevStep}
                disabled={currentStep === 1}
                className={`px-6 py-2 rounded-md ${
                  currentStep === 1
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-600 text-white hover:bg-gray-700'
                }`}
              >
                Précédent
              </button>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/etat-des-lieux')}
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>

                {currentStep < steps.length ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Suivant
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {loading ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EtatDesLieuxForm;
